import {
  PublicClientApplication,
  InteractionRequiredAuthError
} from "@azure/msal-browser";

const clientId = (process.env.VUE_APP_ENTRA_STUDENT_CLIENT_ID || "").trim();
const tenantId = (process.env.VUE_APP_ENTRA_STUDENT_TENANT_ID || "").trim();

/**
 * Always use the current origin (no path/hash) so the same build works for:
 *   http://localhost:8080
 *   https://student.osbod.org
 * These must match the Entra SPA redirect URIs exactly.
 */
function resolveRedirectUri() {
  if (typeof window === "undefined" || !window.location) {
    return "";
  }
  return String(window.location.origin || "").replace(/\/$/, "");
}

let msalInstance = null;
let redirectHandled = false;
let redirectResultPromise = null;

export function isMsalConfigured() {
  return !!(clientId && tenantId);
}

export function getMsalConfigError() {
  if (!clientId || !tenantId) {
    return "Entra ID is niet geconfigureerd (VUE_APP_ENTRA_STUDENT_CLIENT_ID / TENANT_ID).";
  }
  return "";
}

function buildConfig() {
  var redirectUri = resolveRedirectUri();
  return {
    auth: {
      clientId: clientId,
      authority: "https://login.microsoftonline.com/" + tenantId,
      redirectUri: redirectUri,
      postLogoutRedirectUri: redirectUri,
      navigateToLoginRequestUrl: false
    },
    cache: {
      cacheLocation: "localStorage",
      storeAuthStateInCookie: false
    }
  };
}

export function getMsalInstance() {
  if (!isMsalConfigured()) {
    throw new Error(getMsalConfigError());
  }
  if (!msalInstance) {
    msalInstance = new PublicClientApplication(buildConfig());
  }
  return msalInstance;
}

/**
 * Must run once, as early as possible (before Vue mounts),
 * so the auth code query params are not lost.
 */
export function handleRedirectPromise() {
  if (!isMsalConfigured()) {
    return Promise.resolve(null);
  }
  if (!redirectResultPromise) {
    redirectResultPromise = getMsalInstance()
      .handleRedirectPromise()
      .then(function(result) {
        redirectHandled = true;
        return result;
      });
  }
  return redirectResultPromise;
}

export function loginRedirect() {
  return getMsalInstance().loginRedirect({
    scopes: ["openid", "profile", "email"]
  });
}

export function logoutRedirect() {
  if (!isMsalConfigured()) {
    return Promise.resolve();
  }
  var instance = getMsalInstance();
  var account = instance.getAllAccounts()[0];
  return instance.logoutRedirect({
    account: account || undefined
  });
}

export async function getIdToken() {
  var instance = getMsalInstance();
  if (!redirectHandled) {
    await handleRedirectPromise();
  }
  var accounts = instance.getAllAccounts();
  if (!accounts.length) {
    return null;
  }

  var account = accounts[0];
  try {
    var result = await instance.acquireTokenSilent({
      account: account,
      scopes: ["openid", "profile", "email"]
    });
    return result.idToken || null;
  } catch (err) {
    if (err instanceof InteractionRequiredAuthError) {
      await instance.acquireTokenRedirect({
        account: account,
        scopes: ["openid", "profile", "email"]
      });
      return null;
    }
    throw err;
  }
}
